import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from './Header';
import Footer from './Footer';
import './ProfileSettingsPage.css';
import { supabase, getUserById } from './supabaseClient';
import bcrypt from 'bcryptjs';

const ProfileSettingsPage = () => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isUpdatingPassword, setIsUpdatingPassword] = useState(false);
  const [personalInfo, setPersonalInfo] = useState({
    fullName: '',
    email: '',
    phoneNumber: ''
  });

  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [passwordErrors, setPasswordErrors] = useState({});
  const [passwordSuccess, setPasswordSuccess] = useState(false);
  const [profileErrors, setProfileErrors] = useState({});
  const [profileSuccess, setProfileSuccess] = useState(false);

  const [userId, setUserId] = useState(null);

  // Load user data on component mount
  useEffect(() => {
    const loadUserData = async () => {
      try {
        // Check if user is logged in
        const isSignedIn = localStorage.getItem('isSignedIn');
        const userData = localStorage.getItem('user');

        if (!isSignedIn || !userData) {
          // Redirect to login if not signed in
          navigate('/login');
          return;
        }

        const user = JSON.parse(userData);
        setUserId(user.id);

        // Fetch full user data from Supabase
        const fullUserData = await getUserById(user.id);

        if (fullUserData) {
          // Extract phone number (remove +234 prefix if present)
          let phoneNumber = fullUserData.phone || '';
          if (phoneNumber.startsWith('+234')) {
            phoneNumber = phoneNumber.substring(4).trim();
          } else if (phoneNumber.startsWith('234')) {
            phoneNumber = phoneNumber.substring(3).trim();
          }

          setPersonalInfo({
            fullName: fullUserData.name || '',
            email: fullUserData.email || '',
            phoneNumber: phoneNumber
          });
        } else {
          // Fallback to localStorage data
          setPersonalInfo({
            fullName: user.name || '',
            email: user.email || '',
            phoneNumber: user.phone ? user.phone.replace(/^\+?234/, '').trim() : ''
          });
        }
        } catch (error) {
          console.error('Error loading user data:', error);
          // Fallback to localStorage
          const userData = localStorage.getItem('user');
          if (userData) {
            const user = JSON.parse(userData);
            setPersonalInfo({
              fullName: user.name || '',
              email: user.email || '',
              phoneNumber: user.phone ? user.phone.replace(/^\+?234/, '').trim() : ''
            });
          }
        } finally {
        setIsLoading(false);
      }
    };

    loadUserData();
  }, [navigate]);


  const handlePersonalInfoChange = (e) => {
    const { name, value } = e.target;
    setPersonalInfo(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear errors when user starts typing
    if (profileErrors[name]) {
      setProfileErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
    // Clear success message when user starts typing
    if (profileSuccess) {
      setProfileSuccess(false);
    }
  };

  const handlePasswordChange = (e) => {
    const { name, value } = e.target;
    setPasswordData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear errors when user starts typing
    if (passwordErrors[name]) {
      setPasswordErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
    // Clear success message when user starts typing
    if (passwordSuccess) {
      setPasswordSuccess(false);
    }
  };

  const validatePassword = () => {
    const errors = {};

    if (!passwordData.currentPassword) {
      errors.currentPassword = 'Current password is required';
    }

    if (!passwordData.newPassword) {
      errors.newPassword = 'New password is required';
    } else if (passwordData.newPassword.length < 6) {
      errors.newPassword = 'Password must be at least 6 characters';
    }

    if (!passwordData.confirmPassword) {
      errors.confirmPassword = 'Please confirm your new password';
    } else if (passwordData.newPassword !== passwordData.confirmPassword) {
      errors.confirmPassword = 'Passwords do not match';
    }

    if (passwordData.currentPassword && passwordData.newPassword && 
        passwordData.currentPassword === passwordData.newPassword) {
      errors.newPassword = 'New password must be different from current password';
    }

    setPasswordErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSavePersonalInfo = async (e) => {
    e.preventDefault();
    setProfileErrors({});
    setProfileSuccess(false);
    setIsSaving(true);
    
    try {
      if (!userId) {
        setProfileErrors({ general: 'User not found. Please login again.' });
        setTimeout(() => navigate('/login'), 2000);
        setIsSaving(false);
        return;
      }

      // Validate required fields
      if (!personalInfo.fullName || !personalInfo.fullName.trim()) {
        setProfileErrors({ fullName: 'Full name is required' });
        setIsSaving(false);
        return;
      }

      if (!personalInfo.email || !personalInfo.email.trim()) {
        setProfileErrors({ email: 'Email is required' });
        setIsSaving(false);
        return;
      }

      // Validate email format
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(personalInfo.email)) {
        setProfileErrors({ email: 'Please enter a valid email address' });
        setIsSaving(false);
        return;
      }

      // Format phone number with +234 prefix
      let formattedPhone = personalInfo.phoneNumber.trim();
      if (formattedPhone) {
        if (!formattedPhone.startsWith('+')) {
          if (formattedPhone.startsWith('234')) {
            formattedPhone = '+' + formattedPhone;
          } else {
            formattedPhone = '+234' + formattedPhone;
          }
        }
      }

      // Build update object with only changed fields
      const updateData = {
        name: personalInfo.fullName.trim(),
        email: personalInfo.email.toLowerCase().trim(),
        updated_at: new Date().toISOString()
      };

      // Only add phone if it has a value
      if (formattedPhone) {
        updateData.phone = formattedPhone;
      }

      // Update user in Supabase
      const { data, error } = await supabase
        .from('users')
        .update(updateData)
        .eq('id', userId)
        .select()
        .single();

      if (error) {
        console.error('Supabase error:', error);
        // Check for specific error types
        if (error.code === '23505') {
          setProfileErrors({ email: 'This email is already registered. Please use a different email.' });
        } else if (error.message) {
          setProfileErrors({ general: error.message });
        } else {
          setProfileErrors({ general: 'Failed to update profile. Please check your connection and try again.' });
        }
        setIsSaving(false);
        return;
      }

      if (!data) {
        setProfileErrors({ general: 'No data returned from update. Please try again.' });
        setIsSaving(false);
        return;
      }

      // Update localStorage
      const updatedUser = {
        id: data.id,
        name: data.name,
        email: data.email,
        phone: data.phone
      };
      localStorage.setItem('user', JSON.stringify(updatedUser));

      // Show success message
      setProfileSuccess(true);
      setProfileErrors({});

      // Clear success message after 5 seconds
      setTimeout(() => {
        setProfileSuccess(false);
      }, 5000);

    } catch (error) {
      console.error('Error saving personal info:', error);
      setProfileErrors({ general: error.message || 'Failed to update profile. Please try again.' });
    } finally {
      setIsSaving(false);
    }
  };

  const handleUpdatePassword = async (e) => {
    e.preventDefault();
    setPasswordSuccess(false);
    
    // Validate form
    if (!validatePassword()) {
      return;
    }

    setIsUpdatingPassword(true);

    try {
      if (!userId) {
        setPasswordErrors({ general: 'User not found. Please login again.' });
        setTimeout(() => navigate('/login'), 2000);
        setIsUpdatingPassword(false);
        return;
      }

      // Get current user to verify current password
      const { data: userData, error: fetchError } = await supabase
        .from('users')
        .select('password_hash')
        .eq('id', userId)
        .single();

      if (fetchError) {
        throw new Error('Failed to fetch user data. Please try again.');
      }

      if (!userData || !userData.password_hash) {
        throw new Error('User data not found. Please login again.');
      }

      // Verify current password
      const isCurrentPasswordValid = await bcrypt.compare(
        passwordData.currentPassword,
        userData.password_hash
      );

      if (!isCurrentPasswordValid) {
        setPasswordErrors({ currentPassword: 'Current password is incorrect' });
        setIsUpdatingPassword(false);
        return;
      }

      // Check if new password is same as current
      const isSamePassword = await bcrypt.compare(
        passwordData.newPassword,
        userData.password_hash
      );

      if (isSamePassword) {
        setPasswordErrors({ newPassword: 'New password must be different from current password' });
        setIsUpdatingPassword(false);
        return;
      }

      // Hash new password
      const saltRounds = 10;
      const newPasswordHash = await bcrypt.hash(passwordData.newPassword, saltRounds);

      // Update password in Supabase
      const { error: updateError } = await supabase
        .from('users')
        .update({
          password_hash: newPasswordHash,
          updated_at: new Date().toISOString()
        })
        .eq('id', userId);

      if (updateError) {
        throw new Error(updateError.message || 'Failed to update password');
      }

      // Success!
      setPasswordSuccess(true);
      setPasswordErrors({});
      setPasswordData({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      });

      // Clear success message after 5 seconds
      setTimeout(() => {
        setPasswordSuccess(false);
      }, 5000);

    } catch (error) {
      console.error('Error updating password:', error);
      setPasswordErrors({ general: error.message || 'Failed to update password. Please try again.' });
    } finally {
      setIsUpdatingPassword(false);
    }
  };


  if (isLoading) {
    return (
      <div className="profile-settings-page">
        <Header />
        <div style={{ padding: '2rem', textAlign: 'center' }}>
          <p>Loading profile...</p>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="profile-settings-page">
      <Header />
      
      {/* Page Title */}
      <section className="profile-title-section">
        <div className="page-container">
          <div className="profile-title-content">
            <h1 className="profile-page-title">Profile Settings</h1>
            <p className="profile-page-subtitle">
              Manage your personal information and account details.
            </p>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="profile-settings-main">
        <div className="page-container">
          <div className="settings-layout-full">
              {/* Personal Information Panel */}
              <div className="settings-card">
                <div className="settings-card-header">
                  <h2 className="settings-card-title">Personal Information</h2>
                </div>
                <form onSubmit={handleSavePersonalInfo} className="settings-form">
                  {profileSuccess && (
                    <div className="success-message" style={{ 
                      padding: '1rem', 
                      backgroundColor: '#d4edda', 
                      color: '#155724', 
                      borderRadius: '8px', 
                      marginBottom: '1rem',
                      border: '1px solid #c3e6cb'
                    }}>
                      ✓ Profile updated successfully!
                    </div>
                  )}

                  {profileErrors.general && (
                    <div className="error-message" style={{ 
                      padding: '1rem', 
                      backgroundColor: '#f8d7da', 
                      color: '#721c24', 
                      borderRadius: '8px', 
                      marginBottom: '1rem',
                      border: '1px solid #f5c6cb'
                    }}>
                      {profileErrors.general}
                    </div>
                  )}

                  <div className="form-group">
                    <label htmlFor="fullName" className="form-label">
                      Full Name <span className="required">*</span>
                    </label>
                    <input
                      type="text"
                      id="fullName"
                      name="fullName"
                      value={personalInfo.fullName}
                      onChange={handlePersonalInfoChange}
                      className={`form-input ${profileErrors.fullName ? 'error' : ''}`}
                      required
                    />
                    {profileErrors.fullName && (
                      <span className="error-message" style={{ color: '#e53e3e', fontSize: '0.875rem', marginTop: '0.25rem' }}>
                        {profileErrors.fullName}
                      </span>
                    )}
                  </div>

                  <div className="form-group">
                    <label htmlFor="email" className="form-label">
                      Email Address <span className="required">*</span>
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={personalInfo.email}
                      onChange={handlePersonalInfoChange}
                      className={`form-input ${profileErrors.email ? 'error' : ''}`}
                      required
                    />
                    {profileErrors.email && (
                      <span className="error-message" style={{ color: '#e53e3e', fontSize: '0.875rem', marginTop: '0.25rem' }}>
                        {profileErrors.email}
                      </span>
                    )}
                  </div>

                  <div className="form-group">
                    <label htmlFor="phoneNumber" className="form-label">
                      Phone Number <span className="required">*</span>
                    </label>
                    <div className="phone-input-wrapper">
                      <span className="phone-prefix">+234</span>
                      <input
                        type="tel"
                        id="phoneNumber"
                        name="phoneNumber"
                        value={personalInfo.phoneNumber}
                        onChange={handlePersonalInfoChange}
                        placeholder="801 234 5678"
                        className="form-input phone-input"
                        required
                      />
                    </div>
                  </div>

                  <button type="submit" className="save-button" disabled={isSaving}>
                    {isSaving ? 'Saving...' : 'Save Changes'}
                  </button>
                </form>
              </div>

              {/* Password Update Panel */}
              <div className="settings-card">
                <div className="settings-card-header">
                  <h2 className="settings-card-title">Change Password</h2>
                </div>
                <form onSubmit={handleUpdatePassword} className="settings-form">
                  {passwordSuccess && (
                    <div className="success-message" style={{ 
                      padding: '1rem', 
                      backgroundColor: '#d4edda', 
                      color: '#155724', 
                      borderRadius: '8px', 
                      marginBottom: '1rem',
                      border: '1px solid #c3e6cb'
                    }}>
                      ✓ Password updated successfully!
                    </div>
                  )}

                  {passwordErrors.general && (
                    <div className="error-message" style={{ 
                      padding: '1rem', 
                      backgroundColor: '#f8d7da', 
                      color: '#721c24', 
                      borderRadius: '8px', 
                      marginBottom: '1rem',
                      border: '1px solid #f5c6cb'
                    }}>
                      {passwordErrors.general}
                    </div>
                  )}

                  <div className="form-group">
                    <label htmlFor="currentPassword" className="form-label">
                      Current Password <span className="required">*</span>
                    </label>
                    <input
                      type="password"
                      id="currentPassword"
                      name="currentPassword"
                      value={passwordData.currentPassword}
                      onChange={handlePasswordChange}
                      className={`form-input ${passwordErrors.currentPassword ? 'error' : ''}`}
                      required
                    />
                    {passwordErrors.currentPassword && (
                      <span className="error-message" style={{ color: '#e53e3e', fontSize: '0.875rem', marginTop: '0.25rem' }}>
                        {passwordErrors.currentPassword}
                      </span>
                    )}
                  </div>

                  <div className="form-group">
                    <label htmlFor="newPassword" className="form-label">
                      New Password <span className="required">*</span>
                    </label>
                    <input
                      type="password"
                      id="newPassword"
                      name="newPassword"
                      value={passwordData.newPassword}
                      onChange={handlePasswordChange}
                      className={`form-input ${passwordErrors.newPassword ? 'error' : ''}`}
                      required
                    />
                    {passwordErrors.newPassword && (
                      <span className="error-message" style={{ color: '#e53e3e', fontSize: '0.875rem', marginTop: '0.25rem' }}>
                        {passwordErrors.newPassword}
                      </span>
                    )}
                    {passwordData.newPassword && !passwordErrors.newPassword && (
                      <span style={{ color: '#4a5568', fontSize: '0.875rem', marginTop: '0.25rem' }}>
                        Password must be at least 6 characters
                      </span>
                    )}
                  </div>

                  <div className="form-group">
                    <label htmlFor="confirmPassword" className="form-label">
                      Confirm New Password <span className="required">*</span>
                    </label>
                    <input
                      type="password"
                      id="confirmPassword"
                      name="confirmPassword"
                      value={passwordData.confirmPassword}
                      onChange={handlePasswordChange}
                      className={`form-input ${passwordErrors.confirmPassword ? 'error' : ''}`}
                      required
                    />
                    {passwordErrors.confirmPassword && (
                      <span className="error-message" style={{ color: '#e53e3e', fontSize: '0.875rem', marginTop: '0.25rem' }}>
                        {passwordErrors.confirmPassword}
                      </span>
                    )}
                    {passwordData.confirmPassword && 
                     passwordData.newPassword === passwordData.confirmPassword && 
                     !passwordErrors.confirmPassword && (
                      <span style={{ color: '#28a745', fontSize: '0.875rem', marginTop: '0.25rem' }}>
                        ✓ Passwords match
                      </span>
                    )}
                  </div>

                  <button type="submit" className="save-button" disabled={isUpdatingPassword}>
                    {isUpdatingPassword ? 'Updating Password...' : 'Update Password'}
                  </button>
                </form>
              </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ProfileSettingsPage;








